<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import ArrowIcon from '@/components/shared/ArrowIcon.vue'
import organizerRegistrationWizardImage from '@/assets/marketing/organizer-registration-wizard.png'
import organizerFinanceDashboardImage from '@/assets/marketing/organizer-finance-dashboard.png'

const { t } = useI18n()
</script>

<template>
  <section class="bg-white py-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">

      <!-- Spotlight 1: Form builder (image left, text right) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div class="mt-12 rounded-xl overflow-hidden shadow-2xl max-w-2xl mx-auto">
          <p class="text-sm text-slate-400 font-medium">          
            <img :src="organizerRegistrationWizardImage" alt="Organizer Registration Wizard" class="border border-slate-200 shadow-xl w-full" />
          </p>
        </div>
        <div>
          <p class="text-emerald-600 font-semibold text-sm uppercase tracking-widest">
            {{ t('organizers_lp.spotlights.forms_label') }}
          </p>
          <h2 class="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
            {{ t('organizers_lp.spotlights.forms_heading') }}
          </h2>
          <p class="text-slate-500 mt-4 leading-relaxed">
            {{ t('organizers_lp.spotlights.forms_body') }}
          </p>
          <ul class="mt-6 space-y-2">
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('organizers_lp.spotlights.forms_feature1') }}
            </li>
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('organizers_lp.spotlights.forms_feature2') }}
            </li>
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('organizers_lp.spotlights.forms_feature3') }}
            </li>
          </ul>
          <RouterLink
            to="/organizer/onboarding"
            class="inline-flex items-center gap-2 mt-8 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-6 py-3 transition-colors"
          >
            {{ t('organizers_lp.spotlights.forms_cta') }}
            <ArrowIcon direction="right" class="w-4 h-4" />
          </RouterLink>
        </div>
      </div>

      <!-- Spotlight 2: Finance dashboard (text left, image right) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div class="order-2 md:order-1">
          <p class="text-emerald-600 font-semibold text-sm uppercase tracking-widest">
            {{ t('organizers_lp.spotlights.finance_label') }}
          </p>
          <h2 class="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
            {{ t('organizers_lp.spotlights.finance_heading') }}
          </h2>
          <p class="text-slate-500 mt-4 leading-relaxed">
            {{ t('organizers_lp.spotlights.finance_body') }}
          </p>
          <ul class="mt-6 space-y-2">
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('organizers_lp.spotlights.finance_feature1') }}
            </li>
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('organizers_lp.spotlights.finance_feature2') }}
            </li>
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('organizers_lp.spotlights.finance_feature3') }}
            </li>
          </ul>
        </div>
        <div class="order-1 md:order-2 rounded-xl shadow-2xl aspect-video flex items-center justify-center">
          <div class="rounded-xl overflow-hidden shadow-2xl max-w-2xl mx-auto">
            <img :src="organizerFinanceDashboardImage" alt="Organizer Finance Dashboard" class="border border-slate-200 shadow-xl w-full" />
          </div>
        </div>
      </div>

    </div>
  </section>
</template>
